/**
 * PlmTopbarMeta — docks the load-time and unsaved-changes chips in the app's
 * TOP BAR, right after the title (prototype v8's top banner:
 * `PLM – Batch QB-… · 260827002-001.B   Loaded in 640 ms   Unsaved changes`).
 *
 * They belong there rather than on the action bar because they describe the
 * PAGE, not an action on it — the bar is for things you can press. Portalled
 * into AppShell's `#ai-topbar-meta` slot, which collapses to nothing on every
 * page that does not use it.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { LoadTimeChip } from '../../../components/LoadTimeChip';
import { formatCombo, effectiveCombo } from '../../../components/hotkeys';
import './plm.css';

type Props = {
  /** The v13 View · Enter · Validate + QC switch — FIRST after the title, as in
   *  the prototype's banner (title · modes · QC · Loaded in …). */
  modes?: ReactNode;
  loadMs: number | null;
  loading?: boolean;
  /** The open layer has edits that are not saved yet. */
  dirty: boolean;
  /** Step to the previous / next layer of the batch. Undefined at an end,
   *  which disables that arrow rather than hiding it (the pair must not jump
   *  about as you move through the sequence). */
  onPrev?: () => void;
  onNext?: () => void;
  /** What the arrows step through, for their tooltips (default "sample layer"). */
  itemLabel?: string;
  /** Only the ‹ › arrows: no load-time / unsaved chips (a page that shows them on its own bar). */
  arrowsOnly?: boolean;
  /** Show the arrows even when both ends are reached (one record), disabled. */
  alwaysArrows?: boolean;
};

export function PlmTopbarMeta({ modes, loadMs, loading, dirty, onPrev, onNext, itemLabel = 'sample layer', arrowsOnly, alwaysArrows }: Props) {
  // The slot lives in AppShell, which renders BEFORE this page, but resolve it
  // in an effect rather than at render so a remount (or a shell that has not
  // painted yet) cannot capture a null and keep it.
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [navSlot, setNavSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(document.getElementById('ai-topbar-meta'));
    setNavSlot(document.getElementById('ai-topbar-nav'));
  }, []);

  return (
    <>
      {slot && !arrowsOnly && createPortal(
        <>
          {modes}
          <LoadTimeChip ms={loadMs} loading={loading} />
          {dirty && (
            <span className="plm-dirty" title="This layer has edits that are not saved yet">
              <i className="ti ti-point-filled" aria-hidden="true" /> Unsaved changes
            </span>
          )}
        </>,
        slot,
      )}
      {navSlot && (alwaysArrows || onPrev || onNext) && createPortal(
        <span className="dh-arrows plm-navarrows">
          {/* The keys are bound by the page (standing rule 14); the tooltip
              shows the effective combo so a remap reads correctly here too. */}
          <button
            type="button" className="dh-na"
            title={`Previous ${itemLabel} (${formatCombo(effectiveCombo('alt+left'))})`}
            aria-label={`Previous ${itemLabel}`}
            disabled={!onPrev}
            onClick={() => onPrev?.()}
          >&#8249;</button>
          <button
            type="button" className="dh-na dh-na--next"
            title={`Next ${itemLabel} (${formatCombo(effectiveCombo('alt+right'))})`}
            aria-label={`Next ${itemLabel}`}
            disabled={!onNext}
            onClick={() => onNext?.()}
          >&#8250;</button>
        </span>,
        navSlot,
      )}
    </>
  );
}
